document.addEventListener("DOMContentLoaded", () => {
  // ======================================================
  // JIREH AI STYLIST
  // Frontend for:
  //   POST /chat
  //   POST /cart/add
  //   POST /events   (optional analytics; failures never block shopping)
  //
  // API Gateway stage: $default
  // IMPORTANT: Do NOT add /$default to the API URL.
  // ======================================================

  const root = document.getElementById("jireh-ai-stylist");

  if (!root) {
    console.warn("Jireh AI Stylist root element was not found.");
    return;
  }

  // ======================================================
  // CONFIG
  // ======================================================

  const DEFAULT_API_BASE =
    "https://3r8z547047.execute-api.us-east-1.amazonaws.com";

  // Allows Shopify markup to override with:
  // data-api-base="https://....execute-api.us-east-1.amazonaws.com"
  //
  // Falls back to the real API Gateway URL above.
  const API_BASE = (
    root.dataset.apiBase ||
    DEFAULT_API_BASE
  ).replace(/\/+$/, "");

  const CHAT_URL = API_BASE + "/chat";
  const CART_URL = API_BASE + "/cart/add";
  const EVENTS_URL = API_BASE + "/events";

  console.log("Jireh AI API:", API_BASE);

  // ======================================================
  // DOM
  // ======================================================

  const openButton =
    document.getElementById("jireh-ai-open");

  const closeButton =
    document.getElementById("jireh-ai-close");

  const panel =
    document.getElementById("jireh-ai-panel");

  const messages =
    document.getElementById("jireh-ai-messages");

  const form =
    document.getElementById("jireh-ai-form");

  const input =
    document.getElementById("jireh-ai-input");

  const loading =
    document.getElementById("jireh-ai-loading");

  const submitButton =
    form
      ? form.querySelector("button[type='submit']")
      : null;

  if (
    !openButton ||
    !closeButton ||
    !panel ||
    !messages ||
    !form ||
    !input ||
    !loading ||
    !submitButton
  ) {
    console.error(
      "Jireh AI Stylist is missing one or more required DOM elements."
    );
    return;
  }

  // ======================================================
  // SESSION ID
  // ======================================================

  function getSessionId() {
    const storageKey =
      "jireh_stylist_session";

    let sessionId =
      localStorage.getItem(storageKey);

    if (!sessionId) {
      if (
        window.crypto &&
        typeof window.crypto.randomUUID === "function"
      ) {
        sessionId =
          window.crypto.randomUUID();
      } else {
        sessionId =
          "jireh-" +
          Date.now() +
          "-" +
          Math.random()
            .toString(36)
            .substring(2);
      }

      localStorage.setItem(
        storageKey,
        sessionId
      );
    }

    return sessionId;
  }

  const sessionId =
    getSessionId();

  // ======================================================
  // OPTIONAL BUSINESS ANALYTICS
  // Browser-owned events only.
  //
  // Backend should record:
  //   message_sent
  //   product_recommended
  //   add_to_cart
  //   purchase
  //
  // Browser can record:
  //   stylist_opened
  //   product_clicked
  //   size_selected
  //   checkout_clicked
  // ======================================================

  // Only allow links back into this store and images from https URLs,
  // so a bad response from the stylist service can't inject javascript: links.
  function safeStoreUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      // Keep only the path, so the link always opens on the current store domain.
      if (/^https?:$/.test(url.protocol) && /^\/(products|collections)\//.test(url.pathname)) {
        return url.pathname + url.search;
      }
    } catch (e) {}
    return "#";
  }

  function safeImageUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol === "https:") return url.href;
    } catch (e) {}
    return "";
  }

  async function trackStylistEvent(
    eventType,
    data = {}
  ) {
    try {
      const response =
        await fetch(
          EVENTS_URL,
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json"
            },
            body: JSON.stringify({
              event_type:
                eventType,
              session_id:
                sessionId,
              ...data
            }),
            keepalive: true
          }
        );

      if (!response.ok) {
        console.debug(
          "Analytics event was not saved:",
          eventType,
          response.status
        );
      }
    } catch (error) {
      // Analytics must never break the stylist.
      console.debug(
        "Analytics unavailable:",
        eventType,
        error
      );
    }
  }

  // ======================================================
  // OPEN / CLOSE
  // ======================================================

  let stylistOpenedTracked =
    false;

  openButton.addEventListener(
    "click",
    () => {
      panel.hidden = false;
      input.focus();

      // Track one open per page load.
      if (!stylistOpenedTracked) {
        stylistOpenedTracked =
          true;

        trackStylistEvent(
          "stylist_opened"
        );
      }
    }
  );

  closeButton.addEventListener(
    "click",
    () => {
      panel.hidden = true;
    }
  );

  // ======================================================
  // SAFE MESSAGE DISPLAY
  // ======================================================

  function addMessage(
    text,
    role
  ) {
    const wrapper =
      document.createElement("div");

    wrapper.className =
      "jireh-ai__message " +
      (
        role === "user"
          ? "jireh-ai__message--user"
          : "jireh-ai__message--assistant"
      );

    const bubble =
      document.createElement("div");

    bubble.className =
      "jireh-ai__bubble";

    // textContent prevents HTML injection.
    bubble.textContent =
      text || "";

    wrapper.appendChild(
      bubble
    );

    messages.appendChild(
      wrapper
    );

    scrollToBottom();
  }

  function scrollToBottom() {
    messages.scrollTop =
      messages.scrollHeight;
  }

  // ======================================================
  // LOADING STATE
  // ======================================================

  function setLoading(
    isLoading
  ) {
    loading.hidden =
      !isLoading;

    input.disabled =
      isLoading;

    submitButton.disabled =
      isLoading;
  }

  // ======================================================
  // SAFE JSON RESPONSE HELPER
  // ======================================================

  async function readJsonResponse(
    response,
    fallbackMessage
  ) {
    const raw =
      await response.text();

    if (!raw) {
      return {};
    }

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error(
        "Invalid JSON response:",
        raw
      );

      throw new Error(
        fallbackMessage
      );
    }
  }

  // ======================================================
  // CALL AWS POST /chat
  // ======================================================

  async function sendMessage(
    customerMessage
  ) {
    const cleanMessage =
      String(
        customerMessage || ""
      ).trim();

    if (!cleanMessage) {
      return;
    }

    addMessage(
      cleanMessage,
      "user"
    );

    setLoading(true);

    try {
      console.log(
        "POST",
        CHAT_URL,
        {
          message:
            cleanMessage,
          session_id:
            sessionId
        }
      );

      const response =
        await fetch(
          CHAT_URL,
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json"
            },
            body: JSON.stringify({
              // IMPORTANT:
              // Use the function argument.
              message:
                cleanMessage,

              session_id:
                sessionId
            })
          }
        );

      const data =
        await readJsonResponse(
          response,
          "The stylist server returned an invalid response."
        );

      if (!response.ok) {
        console.error(
          "Chat API error:",
          response.status,
          data
        );

        throw new Error(
          data.error ||
          "The stylist request failed."
        );
      }

      if (data.message) {
        addMessage(
          data.message,
          "assistant"
        );
      }

      if (
        Array.isArray(
          data.products
        ) &&
        data.products.length > 0
      ) {
        renderProducts(
          data.products
        );
      }

      if (
        !data.message &&
        (
          !Array.isArray(data.products) ||
          data.products.length === 0
        )
      ) {
        addMessage(
          "I couldn't find a matching product. Try another color, size, style, or price range.",
          "assistant"
        );
      }

      return data;

    } catch (error) {
      console.error(
        "Jireh Stylist Error:",
        error
      );

      addMessage(
        error &&
        error.message &&
        error.message !==
          "The stylist request failed."
          ? "I couldn't complete that search right now. " +
            error.message
          : "I couldn't complete that search right now. Please try again.",
        "assistant"
      );

      return null;

    } finally {
      setLoading(false);
      input.focus();
    }
  }

  // ======================================================
  // FORM
  // ======================================================

  form.addEventListener(
    "submit",
    event => {
      event.preventDefault();

      const message =
        input.value.trim();

      if (!message) {
        return;
      }

      input.value = "";

      sendMessage(
        message
      );
    }
  );

  // ======================================================
  // QUICK PROMPTS
  // ======================================================

  document
    .querySelectorAll(
      ".jireh-ai__quick"
    )
    .forEach(button => {
      button.addEventListener(
        "click",
        () => {
          const prompt =
            button.dataset.prompt;

          if (prompt) {
            sendMessage(
              prompt
            );
          }
        }
      );
    });

  // ======================================================
  // PRODUCT CARDS
  // ======================================================

  function renderProducts(
    products
  ) {
    const container =
      document.createElement("div");

    container.className =
      "jireh-ai__products";

    products.forEach(
      product => {
        const card =
          createProductCard(
            product
          );

        if (card) {
          container.appendChild(
            card
          );
        }
      }
    );

    // Do not add an empty container.
    if (
      container.children.length > 0
    ) {
      messages.appendChild(
        container
      );

      scrollToBottom();
    }
  }

  function createProductCard(
    product
  ) {
    const variants =
      (
        product.matching_variants ||
        product.variants ||
        []
      ).filter(
        variant =>
          variant &&
          variant.available !== false &&
          variant.variant_id
      );

    if (
      variants.length === 0
    ) {
      return null;
    }

    const card =
      document.createElement("div");

    card.className =
      "jireh-ai__product";

    // ====================================================
    // IMAGE
    // ====================================================

    if (product.image) {
      const image =
        document.createElement("img");

      image.className =
        "jireh-ai__product-image";

      image.src =
        safeImageUrl(product.image);

      image.alt =
        product.title ||
        "Jireh Threads product";

      image.loading =
        "lazy";

      card.appendChild(
        image
      );
    }

    const body =
      document.createElement("div");

    body.className =
      "jireh-ai__product-body";

    // ====================================================
    // TITLE
    // ====================================================

    const title =
      document.createElement("div");

    title.className =
      "jireh-ai__product-title";

    title.textContent =
      product.title ||
      "Jireh Threads Product";

    body.appendChild(
      title
    );

    // ====================================================
    // PRICE
    // ====================================================

    const price =
      document.createElement("div");

    price.className =
      "jireh-ai__price";

    body.appendChild(
      price
    );

    // ====================================================
    // VARIANT LABEL
    // ====================================================

    const variantLabel =
      document.createElement("label");

    variantLabel.className =
      "jireh-ai__variant-label";

    variantLabel.textContent =
      "Choose option:";

    body.appendChild(
      variantLabel
    );

    // ====================================================
    // VARIANT SELECT
    // ====================================================

    const select =
      document.createElement("select");

    select.className =
      "jireh-ai__variant-select";

    variants.forEach(
      (variant, index) => {
        const option =
          document.createElement(
            "option"
          );

        option.value =
          variant.variant_id;

        option.dataset.price =
          String(
            variant.price ?? ""
          );

        option.dataset.currency =
          variant.currency ||
          "USD";

        if (variant.size) {
          option.dataset.size =
            variant.size;
        }

        if (variant.color) {
          option.dataset.color =
            variant.color;
        }

        const details = [];

        if (variant.size) {
          details.push(
            "Size " +
            variant.size
          );
        }

        if (variant.color) {
          details.push(
            variant.color
          );
        }

        if (
          details.length === 0 &&
          variant.title
        ) {
          details.push(
            variant.title
          );
        }

        const formattedPrice =
          formatMoney(
            variant.price,
            variant.currency ||
            "USD"
          );

        option.textContent =
          (
            details.length > 0
              ? details.join(" / ")
              : "Product option"
          ) +
          " — " +
          formattedPrice;

        select.appendChild(
          option
        );

        if (index === 0) {
          updatePrice(
            price,
            variant
          );
        }
      }
    );

    select.addEventListener(
      "change",
      () => {
        const selected =
          variants.find(
            variant =>
              variant.variant_id ===
              select.value
          );

        if (selected) {
          updatePrice(
            price,
            selected
          );

          trackStylistEvent(
            "size_selected",
            {
              product_id:
                product.product_id ||
                "",
              product_title:
                product.title ||
                "",
              variant_id:
                selected.variant_id ||
                "",
              size:
                selected.size ||
                "",
              color:
                selected.color ||
                ""
            }
          );
        }
      }
    );

    body.appendChild(
      select
    );

    // ====================================================
    // ACTION AREA
    // ====================================================

    const actions =
      document.createElement("div");

    actions.className =
      "jireh-ai__product-actions";

    // ====================================================
    // VIEW PRODUCT
    // ====================================================

    const view =
      document.createElement("a");

    view.className =
      "jireh-ai__view";

    view.href =
      safeStoreUrl(product.url);

    view.textContent =
      "View Product";

    view.addEventListener(
      "click",
      () => {
        trackStylistEvent(
          "product_clicked",
          {
            product_id:
              product.product_id ||
              "",
            product_title:
              product.title ||
              ""
          }
        );
      }
    );

    // ====================================================
    // ADD TO CART
    // ====================================================

    const addButton =
      document.createElement("button");

    addButton.className =
      "jireh-ai__add";

    addButton.type =
      "button";

    addButton.textContent =
      "Add to Cart";

    addButton.addEventListener(
      "click",
      async () => {
        const variantId =
          select.value;

        if (!variantId) {
          addMessage(
            "Please select a size or product option first.",
            "assistant"
          );

          return;
        }

        // Prevent an invalid Shopify variant from
        // ever being submitted as ProductVariant/0.
        if (
          variantId === "0" ||
          variantId.endsWith("/0") ||
          !variantId.startsWith(
            "gid://shopify/ProductVariant/"
          )
        ) {
          console.error(
            "Invalid Shopify variant ID:",
            variantId
          );

          addMessage(
            "That product option is unavailable. Please choose another option.",
            "assistant"
          );

          return;
        }

        const originalText =
          addButton.textContent;

        addButton.disabled =
          true;

        addButton.textContent =
          "Adding...";

        try {
          const cart =
            await addToCart(
              variantId
            );

          addButton.textContent =
            "Added ✓";

          showCheckout(
            cart,
            product.title
          );

        } catch (error) {
          console.error(
            "Cart error:",
            error
          );

          addButton.textContent =
            originalText;

          addMessage(
            error &&
            error.message
              ? "I couldn't add that item to the cart. " +
                error.message
              : "I couldn't add that item to the cart. Please try again.",
            "assistant"
          );

        } finally {
          setTimeout(
            () => {
              addButton.disabled =
                false;

              if (
                addButton.textContent ===
                "Added ✓"
              ) {
                addButton.textContent =
                  "Add to Cart";
              }
            },
            1800
          );
        }
      }
    );

    actions.appendChild(
      view
    );

    actions.appendChild(
      addButton
    );

    body.appendChild(
      actions
    );

    card.appendChild(
      body
    );

    return card;
  }

  // ======================================================
  // PRICE HELPERS
  // ======================================================

  function updatePrice(
    element,
    variant
  ) {
    element.textContent =
      formatMoney(
        variant.price,
        variant.currency ||
        "USD"
      );
  }

  function formatMoney(
    amount,
    currency
  ) {
    const number =
      Number(amount || 0);

    try {
      return new Intl
        .NumberFormat(
          "en-US",
          {
            style:
              "currency",

            currency:
              currency ||
              "USD"
          }
        )
        .format(
          number
        );
    } catch (error) {
      return "$" +
        number.toFixed(2);
    }
  }

  // ======================================================
  // CALL AWS POST /cart/add
  // ======================================================

  async function addToCart(
    variantId
  ) {
    console.log(
      "POST",
      CART_URL,
      {
        session_id:
          sessionId,
        variant_id:
          variantId
      }
    );

    const response =
      await fetch(
        CART_URL,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json"
          },
          body: JSON.stringify({
            session_id:
              sessionId,
            variant_id:
              variantId
          })
        }
      );

    const data =
      await readJsonResponse(
        response,
        "The cart server returned an invalid response."
      );

    if (!response.ok) {
      console.error(
        "Cart API error:",
        response.status,
        data
      );

      throw new Error(
        data.error ||
        "Unable to add item"
      );
    }

    return data;
  }

  // ======================================================
  // CHECKOUT DISPLAY
  // ======================================================

  function showCheckout(
    cart,
    productTitle
  ) {
    const box =
      document.createElement("div");

    box.className =
      "jireh-ai__checkout-box";

    const success =
      document.createElement("div");

    success.textContent =
      (
        productTitle ||
        "Item"
      ) +
      " added to your cart ✓";

    box.appendChild(
      success
    );

    if (
      cart.subtotal !== undefined
    ) {
      const subtotal =
        document.createElement("div");

      subtotal.style.marginTop =
        "5px";

      subtotal.style.fontWeight =
        "700";

      subtotal.textContent =
        "Subtotal: " +
        formatMoney(
          cart.subtotal,
          cart.currency ||
          "USD"
        );

      box.appendChild(
        subtotal
      );
    }

    if (cart.checkout_url) {
      const checkout =
        document.createElement("a");

      checkout.className =
        "jireh-ai__checkout-button";

      checkout.href =
        cart.checkout_url;

      checkout.textContent =
        "Secure Checkout";

      checkout.addEventListener(
        "click",
        () => {
          trackStylistEvent(
            "checkout_clicked",
            {
              cart_id:
                cart.cart_id ||
                "",
              product_title:
                productTitle ||
                ""
            }
          );
        }
      );

      box.appendChild(
        checkout
      );
    }

    messages.appendChild(
      box
    );

    scrollToBottom();
  }
});